import { Fragment, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  usePropertyField,
  useCollectionBinding,
  usePropertyAddressUrl,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoBlack,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Black variant of the Repli wordmark for use on the footer's light
// background. Source SVG mirrors `repli_white.svg` with the wordmark's
// `fill:#fff` swapped to `#1a1a1a`.
import repliBlackLogoUrl from '../assets/repli_black.svg';
// SocialConnect ("Let's Be Friends" + social icons + photo tiles) now lives at
// the top of the footer so it appears on every page (the footer is rendered
// globally via DefaultLayout) instead of only as a Home-page section.
import { SocialConnect } from './SocialConnect';
// Sitewide price-guarantee tab — gated by custom_show_price_guarantee.
import { PriceGuaranteePopup } from './PriceGuaranteePopup';

// ─── Override note ──────────────────────────────────────────────────────────
// This is a copy-source override of @vibesites/theme-theme-homestead's Footer.
// The only functional change vs. the parent is that <SocialConnect /> is
// rendered at the very top of the <footer>. The footer's own editable fields
// (phone, address, hours, legal links, credit logos) and the RentCafe IQ
// z-index handling are carried over verbatim. Keep this in sync with the parent
// Footer when it changes. SocialConnect keeps its own slot + contentSchema, so
// its title / links / photos stay independently editable.

// ============================================================================
// Types & helpers
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by their canonical day key so we can look each
  // day up when walking the full week below.
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const groups: GroupedHours[] = [];

  // Walk every day of the week. Days without provided hours (e.g. Sunday)
  // render as "Closed" rather than being omitted.
  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the renderer can pick whichever fits the layout.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_black_logo', suggestedFields: ['property_company_mono_black_logo', 'property_company_logo', 'property_company_mono_white_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Property Address, City, ST 00000' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place/business/address
    // fields). An explicit override from the editor still takes precedence.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');

  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  // No dedicated management-company logo? Fall back to the property's secondary/
  // company logo — the same image the header's hamburger drawer shows.
  const secondaryLogo = usePropertyField('property_secondary_logo');
  const managedByImage = managedByLogo.image || secondaryLogo;
  // Property name shown alongside the copyright line in the bottom credit bar.
  const propertyName = usePropertyField('property_name') as string | null;
  // If a true mono-black company logo isn't the one we're rendering (e.g. we
  // fell back to a full-color secondary logo, or the editor picked a color
  // logo), synthesize a mono-black version in CSS via `brightness(0)`, which
  // collapses every pixel to solid black while preserving the alpha channel.
  const monoBlackLogo = usePropertyField('property_company_mono_black_logo') as string | null;
  const isMonoBlack = !!monoBlackLogo && managedByImage === monoBlackLogo;
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // Split the address on its first comma so the street sits on line 1 and the
  // city/state/zip falls to line 2 (matches the stacked layout in the design).
  const addressText = addressLink.text || '123 Property Address, City, ST 00000';
  const commaIndex = addressText.indexOf(',');
  const addressLine1 = commaIndex !== -1 ? addressText.slice(0, commaIndex) : addressText;
  const addressLine2 = commaIndex !== -1 ? addressText.slice(commaIndex + 1).trim() : '';

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe IQ widget is injected at runtime by an external script.
  // It (a) ships under several DOM signatures across versions and (b) sets its
  // own inline `style="z-index: …"` on its outer container after mount, which
  // beats anything a stylesheet declares. The CSS rule further down in the
  // render covers the common selectors as a cheap first pass; this effect is
  // the authoritative fix.
  //
  // Approach: watch document.body for any added/changed node. For each element
  // whose id/class/src/title/name matches a RentCafe-IQ-ish pattern, find the
  // outermost `position: fixed` ancestor (the actual floating container, which
  // is what controls stacking against the rest of the page) and pin its
  // z-index to the max signed 32-bit int with `!important`. `setProperty` with
  // the `important` priority is the only way to override an inline style set
  // by the widget's own JS.
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Patterns covering the DOM signatures Yardi ships the RentCafe IQ /
    // "RentCafe Connect" webchat widget under. The current production build
    // uses the `rcc-app-*` class prefix and the `conversations-webchat-toggle`
    // / `webchat-toggle-button` launcher; older / alternate builds have
    // surfaced under rentcafe / rciq / CRM IQ / Yardi / Yvenue branding, so
    // we keep those entries too as a defense against future rebrands.
    const PATTERNS = [
      /\brcc-app/i,
      /\bwebchat-toggle/i,
      /conversations-webchat/i,
      /rentcafe/i,
      /rciq/i,
      /\bcrm[-_ ]?iq\b/i,
      /\biq[-_ ]?(chat|bot)/i,
      /yardi/i,
      /yvenue/i,
    ];

    const Z = '2147483647';

    function attrHaystack(el: Element): string {
      const id = el.id || '';
      const cls = typeof el.className === 'string'
        ? el.className
        : ((el.className as unknown as { baseVal?: string })?.baseVal || '');
      const src = el.getAttribute('src') || '';
      const title = el.getAttribute('title') || '';
      const name = el.getAttribute('name') || '';
      // Include data-* attributes — Yardi widgets sometimes only brand via those.
      let data = '';
      for (const a of Array.from(el.attributes)) {
        if (a.name.startsWith('data-')) data += ` ${a.name} ${a.value}`;
      }
      return `${id} ${cls} ${src} ${title} ${name}${data}`;
    }

    function looksLikeRciq(el: Element): boolean {
      const hay = attrHaystack(el);
      return PATTERNS.some(p => p.test(hay));
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!looksLikeRciq(el)) return;
      // Pin the matched element itself (in case it's already the fixed container)
      // and also the outermost fixed ancestor (covers wrappers / portals).
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (fixedAncestor && fixedAncestor !== el) pin(fixedAncestor);
    }

    function scanSubtree(root: ParentNode) {
      // Scope the query so we don't enumerate the whole document on every tick.
      const candidates = root.querySelectorAll('iframe, div, aside, section, button');
      for (const c of Array.from(candidates)) processCandidate(c);
      // The root node itself can also be a match (querySelectorAll skips it).
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'src', 'title', 'name', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
    <PriceGuaranteePopup />
    <footer
      className="mh-footer text-[var(--vibe-color-text)]"
      style={{ backgroundColor: 'var(--vibe-color-background)' }}
    >
      {/* ---- Social Connect: sits at the very top of the footer on every page ---- */}
      <SocialConnect />

      {/* ---- Main Footer Content: light surface block ----
          White edges frame the gray block via responsive padding on the
          background-colored wrapper (the footer's white shows through), the
          same boxed treatment the ImageCallouts section uses. */}
      <div className="bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 py-3 sm:py-5 md:py-6">
        <div style={{ backgroundColor: 'var(--vibe-color-surface)' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">

            {/* Contact */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.contact_label"
                defaultValue="<p>Contact</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-text)] transition-colors"
              >
                <EditableToken
                  path="footer.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* Location */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.location_label"
                defaultValue="<p>Location</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-text)] transition-colors block leading-relaxed"
                >
                  <span className="block">{addressLine1}</span>
                  {addressLine2 ? <span className="block">{addressLine2}</span> : null}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Hours */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.hours_label"
                defaultValue="<p>Hours</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              {groupedHours.length > 0 ? (
                // Single grid so the day and time columns line up across every
                // row; `self-center` centers the aligned block under the "Hours" label.
                <dl className="self-center grid grid-cols-[auto_auto] gap-x-6 gap-y-2 text-left font-body text-sm text-[var(--vibe-color-text-muted)]">
                  {groupedHours.map((group, index) => {
                    // Abbreviate multi-day ranges ("Mon - Fri"); keep single
                    // days spelled out ("Saturday") to match the design.
                    const dayDisplay = group.label.includes(' - ') ? group.shortLabel : group.label;
                    return (
                      <Fragment key={index}>
                        <dt className="text-left">{dayDisplay}</dt>
                        <dd className="text-left">{group.hours}</dd>
                      </Fragment>
                    );
                  })}
                </dl>
              ) : (
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)]/60">Office hours will be displayed here.</p>
              )}
            </div>

          </div>
          </div>
        </div>
      </div>

      {/* ---- Compliance Icons + Legal Nav ---- */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-3">
        <div className="flex flex-col items-center gap-4">
          {/* Compliance icons in a row */}
          <div className="flex items-center justify-center gap-28 text-[var(--vibe-color-text)]">
            <FairHousingIcon size={36} color="currentColor" />
            <PetsAllowedIcon size={32} color="currentColor" />
            <WheelchairIcon size={32} color="currentColor" />
          </div>

          {/* Legal nav links with dot separator */}
          <nav className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-body text-[13px] text-[var(--vibe-color-text-muted)]" aria-label="Legal links">
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {accessibilityLink.text || 'Accessibility'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-muted)]/50">·</span>
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-muted)]/50">·</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
          </nav>

          {/* Copyright — year auto-updates from the runtime clock so the
              footer never goes stale. */}
          <span className="text-center font-body text-[10px] text-[var(--vibe-color-text-muted)]">
            © {new Date().getFullYear()} All Rights Reserved.
            {propertyName ? <span className="ml-1">{propertyName}</span> : null}
          </span>
        </div>
      </div>

      {/* ---- Bottom Credit Bar ----
          Single row from `sm` up: Professionally Managed by (left), Powered by /
          Multihub (center), Website Design by (right). Labels share one
          line-height band; logos sit in a matching-height band below so
          both text and marks stay optically aligned across columns. */}
      <div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
          {/* Equal 3-column grid so Powered by stays page-centered even when
              the managed-by / design-by columns have different content widths.
              Below `sm` the three credits stack into one centered column. */}
          <div className="grid w-full grid-cols-1 items-start gap-8 font-body text-[10px] leading-none text-[var(--vibe-color-text-muted)] sm:grid-cols-3 sm:gap-6">

            {/* Managed By */}
            <EditableSlotGroup
              groupPath="footer.managed_by_logo"
              definition={managedByLogoDef}
              className="min-w-0 justify-self-center sm:justify-self-start"
            >
              {managedByImage ? (
                <a
                  href={managedByLogo.url || '#'}
                  target={managedByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 opacity-90 hover:opacity-100 transition-opacity sm:items-start"
                >
                  <RichTextContent
                    path="footer.managed_by_text"
                    defaultValue="<p>Professionally Managed by</p>"
                    as="span"
                    className="flex h-[1em] items-center whitespace-nowrap text-[var(--vibe-color-text-muted)] [&_p]:m-0 [&_p]:leading-none"
                  />
                  <span className="flex h-11 items-end">
                    <VibesitesImage
                      url={managedByImage}
                      alt={managedByLogo.alt || 'Property Management Company'}
                      sizes="180px"
                      className={`h-11 w-auto max-w-[180px] object-contain${isMonoBlack ? '' : ' brightness-0'}`}
                    />
                  </span>
                </a>
              ) : null}
            </EditableSlotGroup>

            {/* Powered By (Multihub) */}
            <EditableSlotGroup
              groupPath="footer.powered_by_logo"
              definition={poweredByLogoDef}
              className="min-w-0 justify-self-center"
            >
              <a
                href={poweredByLogo.url || 'https://www.multihub.io/'}
                target={poweredByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1.5 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered by</p>"
                  as="span"
                  className="flex h-[1em] items-center whitespace-nowrap text-[var(--vibe-color-text-muted)] [&_p]:m-0 [&_p]:leading-none"
                />
                <span className="flex h-11 items-end justify-center">
                  <MultihubLogoMonoBlack height={28} alt={poweredByLogo.alt || 'Multihub'} />
                </span>
              </a>
            </EditableSlotGroup>

            {/* Website Design By (Repli) */}
            <EditableSlotGroup
              groupPath="footer.design_by_logo"
              definition={designByLogoDef}
              className="min-w-0 justify-self-center sm:justify-self-end"
            >
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1.5 opacity-90 hover:opacity-100 transition-opacity sm:items-end"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website Design by</p>"
                  as="span"
                  className="flex h-[1em] items-center whitespace-nowrap text-[var(--vibe-color-text-muted)] [&_p]:m-0 [&_p]:leading-none"
                />
                <span className="flex h-11 items-end">
                  <VibesitesImage
                    url={repliBlackLogoUrl}
                    alt={designByLogo.alt || 'Repli Logo'}
                    height={32}
                    sizes="120px"
                    className="h-8 w-auto max-w-[120px] object-contain"
                    priority
                  />
                </span>
              </a>
            </EditableSlotGroup>

          </div>
        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe IQ chatbot ----
          This is a cheap first pass to cover the common DOM signatures Yardi
          has shipped (iframe, container div, launcher button). The
          authoritative fix lives in the `useEffect` above this `return` — it
          uses a MutationObserver and inline `!important` to handle widget
          variants whose selectors we don't know and to beat the inline
          z-index the widget sets via its own JS after mount. */}
      <style>{`
        /* Clear floating bottom docks (PGP tab, Special Offer, OmniChannel,
           AudioEye) so the last footer rows stay readable when scrolled into view.
           --pgp-dock-height is published by PriceGuaranteePopup (0 when off). */
        .mh-footer {
          padding-bottom: calc(6rem + var(--pgp-dock-height, 0px));
        }
        @media (max-width: 1024px) {
          /* Extra room for OmniChannel stacked above the PGP tab. */
          .mh-footer {
            padding-bottom: calc(9rem + var(--pgp-dock-height, 0px));
          }
        }

        /* Production RentCafe Connect / IQ build (confirmed against live DOM):
           the floating wrap is .rcc-app-cta-wrap, the launcher is
           #conversations-webchat-toggle.webchat-toggle-button. We target the
           wrap (and the inner controls layer) so the entire stacking context
           gets lifted, not just the bubble. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        [class*="rcc-app-"],
        [id*="conversations-webchat"],
        [class*="webchat-toggle"],
        /* Legacy / alternate Yardi signatures, kept as a hedge against
           rebrands and per-property build variants. */
        iframe[src*="rentcafeiq"],
        iframe[src*="rentcafe-iq"],
        iframe[src*="rentcafe.com/iq"],
        iframe[src*="yardi"],
        iframe[src*="yvenue"],
        iframe[title*="RentCafe IQ" i],
        iframe[title*="Rent Cafe IQ" i],
        iframe[title*="CRM IQ" i],
        iframe[title*="webchat" i],
        [id^="rciq"],
        [id*="rentcafeiq"],
        [id*="rentcafe-iq"],
        [id*="rentcafe_iq"],
        [id*="iq-chat"],
        [id*="iqchat"],
        [id*="iq-bot"],
        [id*="iqbot"],
        [class^="rciq-"],
        [class*=" rciq-"],
        [class*="rentcafeiq"],
        [class*="rentcafe-iq"],
        [class*="rentcafe_iq"],
        [class*="iq-chat"],
        [class*="iqchat"],
        [class*="yardi-chat"],
        [class*="yvenue"],
        [data-rciq],
        [data-rentcafeiq] {
          z-index: 2147483647 !important;
        }

        /* AnyoneHome OmniChannel — only stack above the PGP dock on phone/tablet
           where the tab sits under it. Large desktops have room for bottom-right. */
        @media (max-width: 1024px) {
          #omniChannel,
          .omni-ai-chat {
            bottom: calc(6px + var(--pgp-dock-height, 0px)) !important;
            transition: bottom 0.35s ease;
          }
        }
        /* Drop behind the open PGP panel (vendor z-index is ~99999). */
        html[data-pgp-open="true"] #omniChannel,
        html[data-pgp-open="true"] .omni-ai-chat,
        html[data-pgp-open="true"] .omni-ai-chat * {
          z-index: 70 !important;
        }
      `}</style>

    </footer>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>Contact</p>',
    },
    'footer.location_label': {
      label: 'Location Label',
      type: 'richtext',
      defaultValue: '<p>Location</p>',
    },
    'footer.hours_label': {
      label: 'Hours Label',
      type: 'richtext',
      defaultValue: '<p>Hours</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
  },
  slotGroups: {
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
